
import { useState, useEffect } from 'react';

const TIME_CONFIG: Record<string, { queries: string[]; fallback: string; label: string }> = {
  morning: {
    queries: [
      'sunrise mountains', 'golden hour lake', 'dawn foggy valley',
      'morning light forest', 'sunrise ocean beach', 'misty morning hills',
      'sunrise countryside', 'dawn river landscape', 'morning meadow flowers',
      'golden sunrise desert', 'morning clouds peaks', 'sunrise tropical island',
      'dawn autumn forest', 'morning snow mountains', 'sunrise cliffs coast',
      'golden hour vineyard', 'morning waterfall jungle', 'sunrise alpine lake'
    ],
    fallback: 'sunrise landscape',
    label: "Morning",
  },
  afternoon: {
    queries: [
      'sunny meadow landscape', 'blue sky mountains', 'tropical beach turquoise',
      'green valley hills', 'bright forest trail', 'sunny coastal cliffs',
      'alpine meadow summer', 'clear lake mountains', 'sunny desert canyon',
      'bright lavender fields', 'sunny island paradise', 'daylight waterfall',
      'blue ocean aerial', 'sunny vineyard hills', 'bright rice terraces',
      'sunny glacier valley', 'daylight savanna wildlife', 'bright coral reef'
    ],
    fallback: 'sunny landscape nature',
    label: "Afternoon",
  },
  evening: {
    queries: [
      'sunset ocean', 'twilight city skyline', 'golden hour coast',
      'dusk mountains reflection', 'sunset desert dunes', 'evening lake calm',
      'golden sunset savanna', 'twilight bridge river', 'sunset tropical palms',
      'dusk autumn forest', 'evening harbor boats', 'sunset volcanic landscape',
      'golden hour canyon', 'twilight lighthouse coast', 'sunset vineyard',
      'dusk snowy peaks', 'evening cherry blossoms', 'sunset rice paddies'
    ],
    fallback: 'sunset landscape',
    label: "Evening",
  },
  night: {
    queries: [
      'aurora borealis', 'starry sky mountains', 'city lights night',
      'moonlight landscape', 'milky way desert', 'northern lights lake',
      'night sky forest', 'starry ocean beach', 'moonlit snow mountains',
      'aurora reflection water', 'night cityscape skyline', 'stars canyon',
      'moonlight coast waves', 'milky way trees', 'night volcano glow',
      'starry prairie', 'aurora winter cabin', 'moonlit temple'
    ],
    fallback: 'night sky stars',
    label: "Night",
  },
};

export interface UnsplashLocation {
  title?: string;
  name?: string;
  city?: string;
  country?: string;
}

export interface OptimizedImageUrls {
  thumbnail: string;
  mobile: string;
  desktop: string;
  full: string;
}

export interface TimeSlotUnsplashImage {
  url: string;
  urls: OptimizedImageUrls;
  photographer: string;
  photographerUrl: string;
  date: string;
  timeSlot: string;
  location?: UnsplashLocation;
  queryUsed?: string;
  debugInfo?: {
    totalResults: number;
    selectedIndex: number;
    hashSeed: string;
    imageDimensions?: { width: number; height: number };
  };
}

const ACCESS_KEY = 'zevGYo64hCsPYqa7EMLzX_Ue3hRwlzfzBTZcE_EimZk';
const CACHE_VERSION = 3;

function getOptimalImageSize(): { width: number; isMobile: boolean } {
  if (typeof window === 'undefined') return { width: 1920, isMobile: false };
  const dpr = window.devicePixelRatio || 1;
  const isMobile = window.innerWidth < 768;
  return {
    width: Math.min(isMobile ? 800 : 1920, window.innerWidth * dpr),
    isMobile
  };
}

function createOptimizedImageUrls(baseUrl: string): OptimizedImageUrls {
  const cleanUrl = baseUrl.split('?')[0];
  return {
    thumbnail: `${cleanUrl}?w=400&q=70&fm=webp&fit=crop&crop=entropy`,
    mobile: `${cleanUrl}?w=800&q=80&fm=webp&fit=crop&crop=entropy`,
    desktop: `${cleanUrl}?w=1920&q=85&fm=webp&fit=crop&crop=entropy`,
    full: baseUrl
  };
}

function getCurrentTimeSlot(): keyof typeof TIME_CONFIG {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 11) return 'morning';
  if (hour >= 11 && hour < 16) return 'afternoon';
  if (hour >= 16 && hour < 20) return 'evening';
  return 'night';
}

function hashStringToNumber(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash);
}

function getDailyQuery(slot: keyof typeof TIME_CONFIG): { query: string; index: number } {
  const queries = TIME_CONFIG[slot].queries;
  const dateStr = new Date().toISOString().split('T')[0];
  const seed = `${dateStr}-${slot}`;
  const hash = hashStringToNumber(seed);
  return { query: queries[hash % queries.length], index: hash % queries.length };
}

function getTodayKey(slot: string, queryIndex: number) {
  const date = new Date().toISOString().split('T')[0];
  return `timedUnsplash-${date}-${slot}-${queryIndex}`;
}

async function fetchUnsplashImages(query: string): Promise<any> {
  const response = await fetch(
    `https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&orientation=landscape&per_page=30&page=1&client_id=${ACCESS_KEY}`
  );
  if (!response.ok) throw new Error(`Unsplash API error: ${response.status}`);
  return response.json();
}

function extractLocation(img: any, query: string): UnsplashLocation | undefined {
  if (img.location?.name) return { name: img.location.name, title: img.location.name };
  if (img.location?.city || img.location?.country) {
    const title = [img.location.city, img.location.country].filter(Boolean).join(', ');
    return { name: title, title, city: img.location.city, country: img.location.country };
  }
  return undefined;
}

export function useTimeAwareUnsplashBackground() {
  const [backgroundInfo, setBackgroundInfo] = useState<TimeSlotUnsplashImage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchForSlot = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const slot = getCurrentTimeSlot();
        const { query, index: queryIndex } = getDailyQuery(slot);
        const cacheKey = getTodayKey(slot, queryIndex);

        // Check cache
        const cachedItem = localStorage.getItem(cacheKey);
        if (cachedItem) {
          try {
            const cached = JSON.parse(cachedItem);
            if (cached.cacheVersion >= CACHE_VERSION) {
              setBackgroundInfo(cached);
              setIsLoading(false);
              return;
            }
            localStorage.removeItem(cacheKey);
          } catch {
            localStorage.removeItem(cacheKey);
          }
        }

        // Fetch with fallback retry
        let data = await fetchUnsplashImages(query);
        let usedQuery = query;

        if (!data.results?.length) {
          usedQuery = TIME_CONFIG[slot].fallback;
          data = await fetchUnsplashImages(usedQuery);
        }

        if (!data.results?.length) {
          throw new Error('No images found');
        }

        // Deterministic daily selection
        const dateStr = new Date().toISOString().split('T')[0];
        const seed = `${dateStr}-${slot}-${queryIndex}`;
        const hash = hashStringToNumber(seed);
        const idx = hash % data.results.length;
        const image = data.results[idx];

        const optimizedUrls = createOptimizedImageUrls(image.urls.raw);
        const { isMobile } = getOptimalImageSize();
        const bestUrl = isMobile ? optimizedUrls.mobile : optimizedUrls.desktop;

        const result: TimeSlotUnsplashImage = {
          url: bestUrl,
          urls: optimizedUrls,
          photographer: image.user.name,
          photographerUrl: image.user.links.html,
          date: dateStr,
          timeSlot: slot,
          queryUsed: usedQuery,
          location: extractLocation(image, usedQuery),
          debugInfo: {
            totalResults: data.results.length,
            selectedIndex: idx,
            hashSeed: seed,
            imageDimensions: { width: image.width || 0, height: image.height || 0 }
          }
        };

        localStorage.setItem(cacheKey, JSON.stringify({ ...result, cacheVersion: CACHE_VERSION }));

        // Clean old cache
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - 7);
        const cutoffKey = cutoffDate.toISOString().split('T')[0];
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('timedUnsplash-') && key < `timedUnsplash-${cutoffKey}`) {
            localStorage.removeItem(key);
          }
        });

        setBackgroundInfo(result);
      } catch (err: any) {
        setError(err?.message || "Error fetching image");

        const fallbackImages = [
          '/lovable-uploads/8de45ed2-0cb9-4464-a640-d5513ab4c4c3.png',
          '/lovable-uploads/69d8478c-eb2f-4542-8353-09c3789bab30.png',
          '/lovable-uploads/ad659737-78ef-4877-a50e-6d7abab0770a.png'
        ];
        const fallbackIndex = new Date().getHours() % fallbackImages.length;
        const fallbackUrl = fallbackImages[fallbackIndex];

        setBackgroundInfo({
          url: fallbackUrl,
          urls: { thumbnail: fallbackUrl, mobile: fallbackUrl, desktop: fallbackUrl, full: fallbackUrl },
          photographer: 'Default',
          photographerUrl: '#',
          date: new Date().toISOString().split('T')[0],
          timeSlot: getCurrentTimeSlot(),
          queryUsed: 'fallback'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchForSlot();

    const interval = setInterval(() => {
      const currentSlot = getCurrentTimeSlot();
      if (backgroundInfo && backgroundInfo.timeSlot !== currentSlot) {
        fetchForSlot();
      }
    }, 1000 * 60 * 5);

    return () => clearInterval(interval);
  }, []);

  return { backgroundInfo, isLoading, error };
}
